import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Send, Bot, User, RotateCcw, Loader2, Sparkles, TrendingDown,
  CheckCircle2, Link2, Briefcase, Calendar, Lock,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import PurchaseModal from '../components/PurchaseModal';
import { useAuth } from '../context/AuthContext';
import { getSubscriptions } from '../services/api';
import './MockInterviewPage.css';

// AI Asistan Lisansı (AutoApply) aktif değilse mülakat özelliği kilitli —
// bkz. SubscriptionPage.js PRODUCTS['AutoApply']. Yalnızca "Active" durumu
// erişim açar; PENDING/CANCELED/UNPAID kilitli sayılır.
function useHasInterviewAccess() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (!user?.candidateId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getSubscriptions(user.candidateId)
      .then((subs) => {
        if (cancelled) return;
        const active = (subs || []).some((s) =>
          String(s.category || '').toLowerCase() === 'autoapply' &&
          String(s.status || '').toUpperCase() === 'ACTIVE'
        );
        setHasAccess(active);
      })
      .catch(() => { if (!cancelled) setHasAccess(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.candidateId]);

  return { loading, hasAccess };
}

// ── Ücretsiz paket görünümü — özelliği tanıtır, Premium'a yönlendirir ──
const MockInterviewTeaser = ({ onUpgrade }) => (
  <div className="interview-page">
    <div className="interview__container">
      <BackButton />

      <div className="interview-teaser">
        <div className="interview-teaser__avatar">
          <div className="interview-teaser__avatar-ring" />
          <div className="interview-teaser__avatar-core">
            <Bot size={40} />
          </div>
          <span className="interview-teaser__avatar-badge">
            <Sparkles size={12} /> AI Avatar
          </span>
        </div>

        <div className="interview-teaser__eyebrow">
          <TrendingDown size={14} /> İş ilanlarından olumsuz geri dönüş mü alıyorsunuz?
        </div>

        <h1 className="interview-teaser__title">
          Yapay Zeka Avatarı ile <span className="interview-teaser__title-accent">Gerçek Bir Mülakat</span> Deneyimi Yaşayın
        </h1>
        <p className="interview-teaser__subtitle">
          Mülakata girmeden önce kendinizi test edin. Avatarlı AI Mülakat Asistanı size sektörünüze
          özel sorular sorar, cevaplarınızdaki eksik ve güçlü yönleri gösterir; özgüvenle mülakata girin.
        </p>

        <div className="interview-teaser__features">
          <div className="interview-teaser__feature">
            <div className="interview-teaser__feature-icon"><Bot size={18} /></div>
            <div>
              <h3>Avatarlı gerçekçi görüşme</h3>
              <p>Yapay zeka avatarı, başvurduğunuz pozisyona özel sorularla sizinle gerçek bir mülakat gibi konuşur.</p>
            </div>
          </div>
          <div className="interview-teaser__feature">
            <div className="interview-teaser__feature-icon"><CheckCircle2 size={18} /></div>
            <div>
              <h3>Anında geri bildirim</h3>
              <p>Cevaplarınızdaki güçlü ve zayıf yönleri öğrenin, mülakat öncesi eksiklerinizi kapatın.</p>
            </div>
          </div>
          <div className="interview-teaser__feature">
            <div className="interview-teaser__feature-icon"><RotateCcw size={18} /></div>
            <div>
              <h3>Sınırsız pratik</h3>
              <p>İstediğiniz kadar tekrar mülakat yapın, her denemede özgüveniniz artsın.</p>
            </div>
          </div>
        </div>

        <button className="interview-teaser__cta" onClick={onUpgrade}>
          <Lock size={16} /> Premium'a Geç ve Mülakata Başla
        </button>
        <p className="interview-teaser__note">AI Asistan Lisansı aboneliği ile kullanılabilir. Dilediğin zaman iptal edebilirsin.</p>
      </div>
    </div>
  </div>
);

// ── Premium görünüm — gerçek mülakat akışı ──
const MockInterviewActive = () => {
  const [inputMode, setInputMode] = useState('title'); // 'title' | 'link'
  const [jobInput, setJobInput] = useState('');
  const [experience, setExperience] = useState('');
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const canStart = jobInput.trim().length > 0;
  const positionLabel = inputMode === 'link' ? 'girdiğiniz ilan' : jobInput.trim();

  const startInterview = () => {
    if (!canStart) return;
    setStarted(true);
    const expNote = inputMode === 'title' && experience.trim()
      ? ` (aranan tecrübe: ${experience.trim()} yıl)`
      : '';
    setMessages([
      {
        role: 'ai',
        text: `Merhaba! ${positionLabel}${expNote} için mülakat simülasyonuna hoş geldiniz. Hazır olduğunuzda "Başla" yazın.`,
      },
    ]);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      // TODO: API entegrasyonu — avatarlı gerçek zamanlı mülakat servisi bağlanınca
      // burası video/ses akışı başlatan çağrıya çevrilecek.
      await new Promise(r => setTimeout(r, 1500));
      setMessages(prev => [
        ...prev,
        { role: 'ai', text: 'Bu güzel bir cevaptı. Şimdi bir sonraki soruya geçelim: Bu pozisyonda karşılaşabileceğiniz en büyük zorluk ne olabilir ve nasıl çözersiniz?' },
      ]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStarted(false);
    setMessages([]);
    setJobInput('');
    setExperience('');
    setInput('');
  };

  return (
    <div className="interview-page">
      <div className="interview__container">
        <BackButton />
        {!started ? (
          <div className="interview__start">
            <div className="interview__start-icon">
              <MessageSquare size={26} />
            </div>
            <h1 className="interview__title">Deneme Mülakatı</h1>
            <p className="interview__subtitle">Yapay zeka ile mülakat pratiği yap. Bir ilan bağlantısı veya pozisyon adı gir.</p>
            <div className="interview__start-form">
              <div className="interview__mode-toggle">
                <button
                  type="button"
                  className={`interview__mode-btn ${inputMode === 'title' ? 'interview__mode-btn--active' : ''}`}
                  onClick={() => { setInputMode('title'); setJobInput(''); }}
                >
                  <Briefcase size={14} /> Pozisyon
                </button>
                <button
                  type="button"
                  className={`interview__mode-btn ${inputMode === 'link' ? 'interview__mode-btn--active' : ''}`}
                  onClick={() => { setInputMode('link'); setJobInput(''); setExperience(''); }}
                >
                  <Link2 size={14} /> İlan Linki
                </button>
              </div>

              <label className="interview__field-label" htmlFor="interview-job-input">
                {inputMode === 'link' ? 'İlan bağlantısı' : 'Pozisyon'}
              </label>
              <input
                id="interview-job-input"
                className="interview__position-input"
                type={inputMode === 'link' ? 'url' : 'text'}
                placeholder={inputMode === 'link' ? 'https://...' : 'Örn: Satış Uzmanı'}
                value={jobInput}
                onChange={(e) => setJobInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && startInterview()}
              />

              {inputMode === 'title' && (
                <>
                  <div className="interview__suggestions">
                    {['Frontend Developer', 'Satış Uzmanı', 'Muhasebe Uzmanı'].map((s) => (
                      <button key={s} type="button" className="interview__chip" onClick={() => setJobInput(s)}>
                        {s}
                      </button>
                    ))}
                  </div>

                  <label className="interview__field-label" htmlFor="interview-experience">
                    <Calendar size={13} /> Aranan tecrübe yılı <span className="interview__field-optional">(opsiyonel)</span>
                  </label>
                  <input
                    id="interview-experience"
                    className="interview__position-input"
                    type="number"
                    min="0"
                    max="40"
                    placeholder="Örn: 3"
                    value={experience}
                    onChange={(e) => setExperience(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && startInterview()}
                  />
                </>
              )}

              <button className="interview__start-btn" onClick={startInterview} disabled={!canStart}>
                Mülakata Başla
              </button>
            </div>
          </div>
        ) : (
          <div className="interview__chat">
            <div className="interview__chat-header">
              <div>
                <h2 className="interview__chat-title">Mülakat: {inputMode === 'link' ? 'İlan bağlantısı' : jobInput}</h2>
                <p className="interview__chat-hint">Yapay zeka mülakatçınız sizinle konuşuyor</p>
              </div>
              <button className="interview__reset-btn" onClick={reset}>
                <RotateCcw size={14} /> Yeniden Başla
              </button>
            </div>

            <div className="interview__messages">
              {messages.map((msg, i) => (
                <div key={i} className={`interview__msg interview__msg--${msg.role}`}>
                  <div className="interview__msg-avatar">
                    {msg.role === 'ai' ? <Bot size={16} /> : <User size={16} />}
                  </div>
                  <div className="interview__msg-bubble">{msg.text}</div>
                </div>
              ))}
              {loading && (
                <div className="interview__msg interview__msg--ai">
                  <div className="interview__msg-avatar"><Bot size={16} /></div>
                  <div className="interview__msg-bubble interview__msg-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>

            <div className="interview__input-area">
              <input
                className="interview__input"
                placeholder="Cevabınızı yazın..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                disabled={loading}
              />
              <button className="interview__send-btn" onClick={sendMessage} disabled={!input.trim() || loading}>
                <Send size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MockInterviewPage = () => {
  const { loading, hasAccess } = useHasInterviewAccess();
  const [showPurchase, setShowPurchase] = useState(false);

  if (loading) {
    return (
      <div className="interview-page">
        <div className="interview__container">
          <BackButton />
          <div className="interview__loading">
            <Loader2 size={22} className="interview__spinner" />
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <>
        <MockInterviewTeaser onUpgrade={() => setShowPurchase(true)} />
        <PurchaseModal isOpen={showPurchase} onClose={() => setShowPurchase(false)} />
      </>
    );
  }

  return <MockInterviewActive />;
};

export default MockInterviewPage;
